import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ErrorBoundary.module.css';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary?: () => void;
}

export function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleReload = useCallback(() => {
    resetErrorBoundary?.();
  }, [resetErrorBoundary]);

  const handleCopy = useCallback(async () => {
    const text = `${error.name}: ${error.message}\n\n${error.stack ?? ''}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [error]);

  return (
    <div className={styles.container} role="alert">
      <div className={styles.illustration} aria-hidden="true">
        <svg width="100%" viewBox="0 0 680 320" xmlns="http://www.w3.org/2000/svg">
          <title>Broken document illustration</title>
          <desc>An illustration of a document with a jagged crack and a warning triangle</desc>
          <defs>
            <linearGradient id="cardShade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FAECE7" />
              <stop offset="100%" stopColor="#F5C4B3" />
            </linearGradient>
          </defs>
          <g transform="translate(340,160)">
            <circle cx="0" cy="0" r="120" fill="#FAECE7" />
            <g transform="translate(-58,-75)">
              <path d="M0 0 H80 L100 20 V150 A6 6 0 0 1 94 156 H6 A6 6 0 0 1 0 150 Z" fill="#FFFFFF" stroke="#D85A30" strokeWidth="3" />
              <path d="M80 0 V20 H100 Z" fill="#F0997B" />
              <line x1="16" y1="40" x2="60" y2="40" stroke="#F0997B" strokeWidth="6" strokeLinecap="round" />
              <line x1="16" y1="58" x2="84" y2="58" stroke="#F0997B" strokeWidth="6" strokeLinecap="round" />
            </g>
            <path d="M-8 -95 L8 -95 L98 60 A6 6 0 0 1 92 68 L-90 68 A6 6 0 0 1 -96 60 Z" fill="#D85A30" />
            <rect x="-6" y="-70" width="12" height="90" rx="4" fill="#FAECE7" />
            <circle cx="0" cy="42" r="7" fill="#FAECE7" />
          </g>
        </svg>
      </div>

      <h1 className={styles.title}>{t('error.boundaryTitle', 'Something went wrong')}</h1>
      <p className={styles.subtitle}>
        {t('error.boundarySubtitle', 'An unexpected error occurred. Please try again, and if the problem persists, contact support.')}
      </p>

      <div className={styles.actions}>
        <button className={styles.reloadBtn} onClick={handleReload} aria-label={t('error.tryAgain', 'Try again')}>
          {t('error.tryAgain', 'Try again')}
        </button>
        <button
          className={styles.detailsToggle}
          onClick={() => setShowDetails((v) => !v)}
          aria-expanded={showDetails}
          aria-controls="error-details-panel"
        >
          {showDetails ? t('error.hideDetails', 'Hide details') : t('error.showDetails', 'Technical details')}
          <svg
            className={`${styles.chevron} ${showDetails ? styles.chevronOpen : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M3 4.5 L6 7.5 L9 4.5" />
          </svg>
        </button>
      </div>

      {showDetails && (
        <div className={styles.detailsPanel} id="error-details-panel" role="region" aria-label={t('error.technicalDetails', 'Technical details')}>
          <div className={styles.detailsHeader}>
            <span className={styles.detailsLabel}>{t('error.errorDetails', 'Error details')}</span>
            <button
              className={`${styles.copyBtn} ${copied ? styles.copyBtnCopied : ''}`}
              onClick={handleCopy}
              aria-label={copied ? t('error.copied', 'Copied') : t('error.copyToClipboard', 'Copy to clipboard')}
            >
              {copied ? (
                <>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M2.5 6 L5 8.5 L9.5 3" />
                  </svg>
                  {t('error.copied', 'Copied')}
                </>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3.5" y="2.5" width="7" height="8" rx="1" />
                    <path d="M8.5 2.5v-1a1 1 0 00-1-1h-5a1 1 0 00-1 1v7a1 1 0 001 1h1" />
                  </svg>
                  {t('error.copyToClipboard', 'Copy')}
                </>
              )}
            </button>
          </div>
          <pre className={styles.stackFrame} tabIndex={0}>
            <span className={styles.errorName}>{error.name}</span>
            <div className={styles.errorMessage}>{error.message}</div>
            <div className={styles.errorStack}>{error.stack?.split('\n').slice(1).join('\n')}</div>
          </pre>
        </div>
      )}
    </div>
  );
}
